import type { Project } from './types';
import type { AdvisorProjectDetail, BookedVisit } from './advisor-api';
import type { PrototypeProjectDetail } from './prototype-catalog';

export function legalStatusesFromDetail(d: AdvisorProjectDetail): Array<{ label: string; value: string }> {
  const rows: Array<{ label: string; value: string }> = [];
  if (d.rera_number) rows.push({ label: 'RERA', value: d.rera_number });
  if (d.khata) rows.push({ label: 'Khata', value: d.khata });
  if (d.na_status) rows.push({ label: 'NA conversion', value: d.na_status });
  if (d.ec_status) rows.push({ label: 'EC / Khata path', value: d.ec_status });
  if (d.loan_eligibility) rows.push({ label: 'Loan eligibility', value: d.loan_eligibility });
  if (d.possession) rows.push({ label: 'Possession', value: d.possession });
  return rows;
}

export function mergeLiveDetail(
  base: PrototypeProjectDetail,
  live: AdvisorProjectDetail,
): PrototypeProjectDetail {
  const statuses = legalStatusesFromDetail(live);
  const configs = live.configurations ?? [];
  const firstConfig = configs[0];
  const price =
    live.starting_price_display?.trim() ||
    firstConfig?.price_display ||
    base.price;

  return {
    ...base,
    name: live.name || base.name,
    location: live.micro_market || base.location,
    price,
    possession: live.possession ?? base.possession,
    rera: live.rera_number ?? base.rera,
    banks: live.loan_eligibility ?? base.banks,
    statuses: statuses.length ? statuses : base.statuses,
    dummy: false,
    live: true,
    summary: live.summary,
    projectType: live.project_type,
    configurations: configs.map((c) => ({
      unitType: c.unit_type,
      priceDisplay: c.price_display,
      priceMinInr: c.price_min_inr,
      ...(c.size_display ? { sizeDisplay: c.size_display } : {}),
    })),
    locationIntel: live.location,
    faqs: live.faqs,
    ...(live.market_intel ? { marketIntel: live.market_intel } : {}),
    cfg: firstConfig
      ? {
          label: firstConfig.unit_type,
          carpet: base.cfg.carpet,
          superb: base.cfg.superb,
          facing: base.cfg.facing,
          ceiling: base.cfg.ceiling,
          parkIncluded: base.cfg.parkIncluded,
        }
      : base.cfg,
    timeline: statuses.length
      ? []
      : base.timeline,
    insight: live.summary ?? (statuses.length ? 'Live legal snapshot from NayaDesk.' : base.insight),
  };
}

export function projectWithLiveDetail(
  base: Project | PrototypeProjectDetail,
  live?: AdvisorProjectDetail,
): PrototypeProjectDetail {
  const enriched =
    'dummy' in base && base.dummy !== undefined
      ? (base as PrototypeProjectDetail)
      : ({
          ...base,
          dummy: true,
          psf: 0,
          mktPsf: 0,
          cagr: 0,
          mktCagr: 0,
          yieldPct: 0,
          history: [],
          rera: '',
          insight: '',
          banks: '',
          cfg: {
            label: base.bhk || '—',
            carpet: 0,
            superb: 0,
            facing: '—',
            ceiling: '—',
            parkIncluded: false,
          },
          statuses: [],
          timeline: [],
        } satisfies PrototypeProjectDetail);

  if (!live) return enriched;
  return mergeLiveDetail(enriched, live);
}

export function emiBasisLakhs(project: PrototypeProjectDetail): number {
  const fromConfig = project.configurations?.[0]?.priceMinInr;
  if (fromConfig && fromConfig > 0) return fromConfig / 100_000;
  const m = /([\d.]+)\s*L/i.exec(project.price);
  if (m) return parseFloat(m[1]!);
  return 50;
}

export function projectFromBookedVisit(visit: BookedVisit, catalog: Project[]): Project {
  const hit = catalog.find((p) => p.id === visit.projectId);
  if (hit) return hit;
  return {
    id: visit.projectId,
    name: visit.projectName,
    builder: 'Naya',
    location: '',
    price: '',
    bhk: '',
    fit: '',
    accent: '#9a7734',
  };
}
